import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Filter, ArrowUpDown, X } from 'lucide-react';
import { useInvestmentFilters } from './hooks/useInvestmentFilters';
import { INVESTMENT_TYPE_LABELS } from './investments/investmentTableConstants';
import InvestmentDetailsRow from './investments/InvestmentDetailsRow';

const SortableHead = ({ label, field, toggleSort, className = '' }) => (
  <TableHead className={`cursor-pointer hover:bg-slate-50 ${className}`} onClick={() => toggleSort(field)}>
    <div className={`flex items-center space-x-1 ${className.includes('text-right') ? 'justify-end' : ''}`}>
      <span>{label}</span><ArrowUpDown className="w-3 h-3" />
    </div>
  </TableHead>
);

const InvestmentDetailsTable = ({ investments, onEdit, onDelete, isReadOnly = false }) => {
  const {
    searchQuery, setSearchQuery, typeFilter, setTypeFilter,
    uniqueTypes, filteredInvestments, toggleSort, clearFilters, hasFilters,
  } = useInvestmentFilters(investments);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Investment Details</span>
          <Badge variant="outline">{filteredInvestments.length} {filteredInvestments.length === 1 ? 'investment' : 'investments'}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search investments..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[200px]"><Filter className="w-4 h-4 mr-2" /><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {uniqueTypes.map(type => (<SelectItem key={type} value={type}>{INVESTMENT_TYPE_LABELS[type] || type}</SelectItem>))}
            </SelectContent>
          </Select>
          {hasFilters && <Button variant="outline" onClick={clearFilters}><X className="w-4 h-4 mr-2" />Clear</Button>}
        </div>

        {filteredInvestments.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <p className="text-lg mb-2">No investments found</p>
            <p className="text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow>
                  <SortableHead label="Name" field="name" toggleSort={toggleSort} className="max-w-[180px]" />
                  <TableHead>Type</TableHead>
                  <TableHead>Institution / Manager</TableHead>
                  <TableHead>Purchase Date</TableHead>
                  <TableHead>Rate / Coupon</TableHead>
                  <TableHead>Maturity</TableHead>
                  <SortableHead label="Invested" field="amount" toggleSort={toggleSort} className="text-right" />
                  <SortableHead label="Current Value" field="current_value" toggleSort={toggleSort} className="text-right" />
                  <SortableHead label="Return" field="return" toggleSort={toggleSort} className="text-right" />
                  <SortableHead label="Added" field="created_at" toggleSort={toggleSort} />
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvestments.map((investment) => (
                  <InvestmentDetailsRow key={investment.investment_id} investment={investment} isReadOnly={isReadOnly} onEdit={onEdit} onDelete={onDelete} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InvestmentDetailsTable;
